import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { PUSH_PRIMER_DEFAULTS, PUSH_PRIMER_DEFAULT_LATER_DAYS } from "@/lib/pushPrimer";

const LANGS = ["fr", "en", "es", "pt"];
const FIELDS = [
  { key: "title", label: "Titre" },
  { key: "body", label: "Message", multiline: true },
  { key: "cta", label: "Bouton « Activer »" },
  { key: "later", label: "Bouton « Plus tard »" },
];

// Admin › Notifications › carte « Activer les notifications ? » (app) : interrupteurs iOS / Android, délai « Plus tard »,
// textes 4 langues (vide = texte par défaut de l'app). Enregistré dans settings.push_primer (PATCH /admin/settings).
export const PushPrimerCardSettings = ({ canManage = true }) => {
  const { setConfig, lang } = useApp();
  const [pp, setPp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setPp(data.settings?.push_primer || {})).catch(() => setPp({}));
  }, []);

  const setText = (field, code, v) => setPp((p) => ({ ...p, texts: { ...(p.texts || {}), [field]: { ...((p.texts || {})[field] || {}), [code]: v } } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", { push_primer: pp });
      setPp(data.settings?.push_primer || {});
      toast.success("Carte « Activer les notifications ? » enregistrée");
      try { const c = await api.get(`/config/public?lang=${lang}`); setConfig(c.data); } catch { /* noop */ }
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!pp) return null;
  const iosOn = pp.ios_enabled !== false;
  const androidOn = pp.android_enabled === true;
  return (
    <div className="yalola-card p-5 mb-4 break-inside-avoid" data-testid="push-primer-settings-card">
      <div className="flex items-center gap-2 mb-1"><BellRing size={18} style={{ color: "var(--yalola-primary)" }} /><div className="font-bold font-display text-lg">Carte « Activer les notifications ? » (app)</div></div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
        Petite carte Yalola affichée <b>avant</b> le dialogue système de notifications (au tap sur la cloche ou après la première publication / commentaire / abonnement).
        Sur iOS le dialogue système ne peut être montré qu'une fois : la carte évite de gâcher cette chance. « Plus tard » re-propose la carte plus tard.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5" style={{ borderColor: "var(--yalola-border)" }}>
          <span className="text-sm font-medium">iOS</span>
          <Switch checked={iosOn} disabled={!canManage} onCheckedChange={(v) => setPp({ ...pp, ios_enabled: v })} data-testid="push-primer-ios-switch" />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5" style={{ borderColor: "var(--yalola-border)" }}>
          <span className="text-sm font-medium">Android</span>
          <Switch checked={androidOn} disabled={!canManage} onCheckedChange={(v) => setPp({ ...pp, android_enabled: v })} data-testid="push-primer-android-switch" />
        </div>
        <div>
          <Label className="text-xs">« Plus tard » : re-proposer après (jours)</Label>
          <Input type="number" min={1} max={30} value={pp.later_days ?? PUSH_PRIMER_DEFAULT_LATER_DAYS} disabled={!canManage}
            onChange={(e) => setPp({ ...pp, later_days: e.target.value })} data-testid="push-primer-later-days" />
        </div>
      </div>
      <p className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }}>
        Android désactivé = comportement actuel (demande système au login). Activé = même parcours qu'iOS, effectif au prochain build Android.
      </p>
      <div className="mt-4 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} data-testid={`push-primer-field-${f.key}`}>
            <Label className="text-xs font-semibold">{f.label}</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-1">
              {LANGS.map((code) => {
                const value = pp.texts?.[f.key]?.[code] || "";
                const Comp = f.multiline ? Textarea : Input;
                return (
                  <div key={code} className="flex items-start gap-2">
                    <span className="text-[10px] font-bold w-7 shrink-0 pt-2.5" style={{ color: "var(--yalola-text-muted)" }}>{code.toUpperCase()}</span>
                    <div className="flex-1">
                      <Comp value={value} rows={f.multiline ? 3 : undefined} disabled={!canManage}
                        onChange={(e) => setText(f.key, code, e.target.value)}
                        placeholder={PUSH_PRIMER_DEFAULTS[f.key][code]} data-testid={`push-primer-text-${f.key}-${code}`} />
                      <span className="text-[9px] font-semibold" style={{ color: value.trim() ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }} data-testid={`push-primer-state-${f.key}-${code}`}>
                        {value.trim() ? "Personnalisé" : "Par défaut"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <Button className="pill pill-primary" onClick={save} disabled={saving || !canManage} data-testid="push-primer-save">{saving ? "Enregistrement..." : "Enregistrer"}</Button>
      </div>
    </div>
  );
};
